/**
 * Two small pieces every channel sheet shares, so the difference between what's public and
 * what's private reads the same on every platform:
 *
 *   PlatformCta  the ONE way to connect a platform: a big slab in the platform's own ink,
 *                with its mark (Connect YouTube, Reconnect YouTube, Add TikTok).
 *   PrivacyTag   "Public" beside what anyone can see (the profile, the posts, the sponsors in
 *                them); "Only you" beside what only the creator sees (YouTube's Studio numbers).
 *   WhatYouGet   what connecting a platform adds, item by item, each tagged Public or Only you,
 *                with what it can't give said plainly — shown before anyone is asked to connect.
 */
import React from 'react';
import { PlatIcon } from './sections';

export const PlatformCta: React.FC<{
  id: string;
  label: string;
  onClick?: () => void;
  busy?: boolean;
  busyLabel?: string;
  type?: 'button' | 'submit';
  disabled?: boolean;
}> = ({ id, label, onClick, busy = false, busyLabel, type = 'button', disabled = false }) => (
  <button type={type} className="br-platform-cta" data-platform={id} onClick={onClick}
    disabled={busy || disabled} aria-busy={busy || undefined}>
    <PlatIcon id={id} size={20} fill="currentColor" />
    <span>{busy && busyLabel ? busyLabel : label}</span>
  </button>
);

const LockGlyph: React.FC = () => (
  <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false">
    <path d="M3.5 5.2V3.9a2.5 2.5 0 0 1 5 0v1.3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <rect x="2.2" y="5.2" width="7.6" height="5.4" rx="1" fill="currentColor" />
  </svg>
);

export const PrivacyTag: React.FC<{ kind: 'public' | 'private' }> = ({ kind }) => (
  <span className={`br-privacy br-privacy--${kind}`}
    title={kind === 'private' ? 'Only you can see this. It never appears on your public profile.' : 'Anyone who looks you up on Bearri can see this.'}>
    {kind === 'private' && <LockGlyph />}{kind === 'private' ? 'Only you' : 'Public'}
  </span>
);

type Gain = { text: string; kind: 'public' | 'private' };
/** What adding each platform gives. Bearri reads; it never follows anyone. */
export const WHAT_YOU_GET: Record<string, { gains: Gain[]; limit?: string }> = {
  youtube: {
    gains: [
      { text: 'Views, watch time and subscribers by day', kind: 'private' },
      { text: 'Who watches: age, gender and countries', kind: 'private' },
      { text: 'Where your views come from, and how each new video started', kind: 'private' },
      { text: 'The sponsors in your videos, found and kept up to date', kind: 'public' },
    ],
  },
  instagram: {
    gains: [
      { text: 'The sponsors in your posts, found and kept up to date', kind: 'public' },
      { text: 'Your followers and recent posts', kind: 'public' },
    ],
    limit: "Reach and audience aren't included yet: they need an Instagram permission Bearri doesn't ask for.",
  },
  tiktok: {
    gains: [
      { text: 'The sponsors in your public posts', kind: 'public' },
      { text: 'Your followers and recent posts', kind: 'public' },
    ],
    limit: "TikTok doesn't share private stats with Bearri yet.",
  },
  twitch: {
    gains: [
      { text: 'The sponsors in your public streams', kind: 'public' },
      { text: 'Your followers and recent streams', kind: 'public' },
    ],
    limit: "Twitch doesn't share private stats with Bearri yet.",
  },
};

export const WhatYouGet: React.FC<{ id: string; heading: string; only?: 'private' | 'public'; showLimit?: boolean }> = ({
  id, heading, only, showLimit = true,
}) => {
  const entry = WHAT_YOU_GET[id];
  if (!entry) return null;
  const gains = only ? entry.gains.filter(g => g.kind === only) : entry.gains;
  return (
    <div className="br-get">
      <p className="br-get__head">{heading}</p>
      <ul className="br-get__list">
        {gains.map(g => <li key={g.text}><span>{g.text}</span><PrivacyTag kind={g.kind} /></li>)}
      </ul>
      {showLimit && entry.limit && <p className="br-caption mt-2">{entry.limit}</p>}
    </div>
  );
};
